/**
 * Normalises a form date value to a full ISO-8601 timestamp for backend fields
 * declared as `format: date-time`. Jackson answers a bare calendar date such as
 * "2025-04-22" with 400 "Failed to read request", so a day-only value is pinned
 * to UTC midnight of that same calendar day (no timezone shift).
 *
 * Accepts the two shapes the registration form can hold: "YYYY-MM-DD" (from the
 * UNP lookup) and a hand-typed "DD.MM.YYYY" / "DD/MM/YYYY". Anything already
 * carrying a time component is trusted as-is.
 *
 * Returns `undefined` for an empty value so the caller can omit the key instead
 * of sending an empty string into a date-time field.
 */
export const toBackendDateTime = (
  value?: string | null
): string | undefined => {
  const trimmed = value?.trim()
  if (!trimmed) return undefined

  if (trimmed.includes('T')) return trimmed

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return `${trimmed}T00:00:00.000Z`
  }

  const dotted = /^(\d{2})[./](\d{2})[./](\d{4})$/.exec(trimmed)
  if (dotted) {
    const [, dd, mm, yyyy] = dotted
    return `${yyyy}-${mm}-${dd}T00:00:00.000Z`
  }

  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? trimmed : parsed.toISOString()
}
